import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import MaterialGrid from '@material-ui/core/Grid';
import PrimaryButton from 'common/components/buttons/primaryButton';
import { InputLabelOutlined } from 'common/components/fields/InputLabelOutlined';
import { SelectLabel } from 'common/components/fields/SelectLabel';
import LoadingProgress from 'common/components/progress/loading';
import { DDDMask, phoneWithoutDDDMask } from 'helpers/masks';
import { FORM_RULES } from 'helpers/validations';
import { getAdditionalData } from 'modules/owner/pages/signupCompany/signupCompanyActions.js';
import React, { useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, Form, getFormValues, reduxForm } from 'redux-form';
import { putGatewayInfo, saveGatewayInfo } from '../profileActions';
import {
  DadosEmpresaFields,
  DadosPessoaFisicaFields,
  RepresentanteLegalFields,
  SituacaoRecebedor,
} from './camposRecebedor';
import {
  campoTravado,
  formularioParaPayload,
  normalizarAgencia,
  normalizarConta,
  normalizarDigito,
  TIPOS_CONTA,
} from './recebedorFormulario';

const OBRIGATORIO = [FORM_RULES.required];
const VALIDA_EMAIL = [FORM_RULES.required, FORM_RULES.email];
const VALIDA_URL = [FORM_RULES.required, FORM_RULES.url];

let GatewayForm = function (props) {
  const { formValues } = props;
  const establishment = props.auth.user.establishments[0];
  const [isDisabled, setDisabled] = React.useState(false);

  // Dados relidos da Pagar.me por getPagarmeDetail: o que já foi enviado fica
  // travado, porque a v5 não deixa editar campo preenchido.
  const recebedor = props.profile.pagarmeData?.recebedor;
  const valoresIniciais = props.profile.pagarmeData?.data;
  const travado = (caminho) => campoTravado(valoresIniciais, caminho, recebedor);
  const tipoDeConta = formValues?.create_account?.bankAccountType;

  useEffect(() => {
    if (establishment.digital_account) {
      setDisabled(true);
    } else if (props.auth.user.email) {
      props.change('create_account.email', props.auth.user.email);
    }
    props.getAdditionalData();
  }, []);

  useEffect(() => {
    const endereco = establishment.address;
    if (isDisabled || tipoDeConta !== 'pj' || !endereco) return;
    if (formValues?.create_account?.corporation?.mainAddress?.zipCode) return;
    props.change('create_account.corporation.mainAddress', {
      zipCode: endereco.zip_code,
      street: endereco.street || endereco.line_1,
      streetNumber: endereco.number,
      complementary: endereco.complement,
      neighborhood: endereco.neighborhood,
      city: endereco.city,
      state: endereco.state,
    });
  }, [tipoDeConta]);

  function onSubmit(values) {
    const payload = formularioParaPayload(values, establishment);
    if (!isDisabled) {
      props.saveGatewayInfo(payload);
    } else {
      props.putGatewayInfo(payload, establishment.id);
    }
  }

  if (props.signupCompany.additionalDataLoading || props.profile.pagarmeLoading) {
    return <LoadingProgress />;
  }
  const { banks } = props.signupCompany.additionalData;
  const sortedBanks = banks.sort(function (a, b) {
    if (a.number < b.number) {
      return -1;
    }
    if (a.number > b.number) {
      return 1;
    }
    return 0;
  });
  const sufixoPj = isMobile ? '' : tipoDeConta === 'pj' ? ' (PJ)' : '';
  return (
    <Form role="form" onSubmit={props.handleSubmit(onSubmit)} noValidate>
      {isDisabled ? <SituacaoRecebedor recebedor={recebedor} /> : null}
      <MaterialGrid item container xs={12} style={{ padding: '12px 0' }}>
        <Typography variant="body1">Dados da empresa para o cadastro no Pagar.me</Typography>
      </MaterialGrid>
      <MaterialGrid container spacing={3}>
        <MaterialGrid item xs={12} md={6}>
          <Field
            component={InputLabelOutlined}
            label="E-mail"
            name="create_account.email"
            validate={VALIDA_EMAIL}
            required
            disabled={travado('create_account.email')}
            margin="normal"
          />
        </MaterialGrid>
        <MaterialGrid item xs={4} md={2}>
          <Field
            component={InputLabelOutlined}
            label="DDD"
            name="create_account.phone.ddd"
            validate={OBRIGATORIO}
            required
            disabled={travado('create_account.phone.ddd')}
            margin="normal"
            {...DDDMask}
          />{' '}
        </MaterialGrid>
        <MaterialGrid item xs={8} md={4}>
          <Field
            component={InputLabelOutlined}
            label="Número"
            name="create_account.phone.number"
            validate={OBRIGATORIO}
            required
            disabled={travado('create_account.phone.number')}
            margin="normal"
            {...phoneWithoutDDDMask}
          />
        </MaterialGrid>
        {isMobile ? (
          <MaterialGrid item container xs={12} style={{ paddingBottom: 0 }}>
            <Typography variant="body2">
              Sua empresa na rede (site, linkedIn, Instagram, Facebook):
            </Typography>
          </MaterialGrid>
        ) : null}
        <MaterialGrid item xs={12}>
          <Field
            component={InputLabelOutlined}
            label={`URL${isMobile ? '' : ' (site, linkedin, instagram, facebook)'}`}
            name="create_account.businessUrl"
            validate={VALIDA_URL}
            required
            disabled={travado('create_account.businessUrl')}
            margin="normal"
          />
        </MaterialGrid>
      </MaterialGrid>
      <MaterialGrid item container xs={12} style={{ paddingTop: '12px' }}>
        <Typography variant="body2">Selecione o tipo de conta</Typography>
      </MaterialGrid>
      <MaterialGrid container spacing={3}>
        <MaterialGrid item xs={12} md={6}>
          <Field
            component={SelectLabel}
            label="Tipo de conta"
            name="create_account.bankAccountType"
            validate={OBRIGATORIO}
            options={[
              {
                value: 'pf',
                label: 'Pessoa Física',
              },
              {
                value: 'pj',
                label: 'Pessoa Jurídica',
              },
            ]}
            required
            margin="normal"
            disabled={isDisabled}
          />
        </MaterialGrid>
      </MaterialGrid>
      {tipoDeConta === 'pj' ? (
        <>
          <DadosEmpresaFields
            prefixo="create_account"
            travado={travado}
            establishment={establishment}
          />
          <RepresentanteLegalFields prefixo="managingPartners" travado={travado} />
        </>
      ) : tipoDeConta === 'pf' ? (
        <DadosPessoaFisicaFields
          prefixo="create_account"
          travado={travado}
          cpfTravado={isDisabled}
        />
      ) : null}
      {tipoDeConta ? (
        <>
          <MaterialGrid item container xs={12} style={{ padding: '12px 0' }}>
            <Typography variant="body1">Dados bancários para recebimento de vendas</Typography>
            {isMobile && tipoDeConta === 'pj' ? (
              <Typography variant="caption">
                Atenção: Preencha os campos com as informações relacionadas à sua conta PJ
              </Typography>
            ) : null}
            {tipoDeConta === 'pj' ? (
              <Typography variant="body2" style={{ width: '100%' }}>
                Titular: {establishment.social_name || '-'}
              </Typography>
            ) : null}
          </MaterialGrid>
          <MaterialGrid container spacing={3}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={SelectLabel}
                label={`Banco${sufixoPj}`}
                name="create_account.bankAccount.bankNumber"
                validate={OBRIGATORIO}
                options={sortedBanks.map((item) => ({
                  label: `${item.number} ${item.name}`,
                  value: item.number,
                }))}
                required
                margin="normal"
              />
            </MaterialGrid>
            <MaterialGrid item container xs={12} style={{ paddingBottom: 0 }}>
              <Typography variant="body2">Dados da agência:</Typography>
            </MaterialGrid>
            <MaterialGrid item xs={7} md={6}>
              <Field
                component={InputLabelOutlined}
                label={`Número${sufixoPj}*`}
                name="create_account.bankAccount.agencyNumber"
                validate={OBRIGATORIO}
                normalize={normalizarAgencia}
                required
                margin="normal"
              />
            </MaterialGrid>
            <MaterialGrid item xs={5} md={6}>
              <Field
                component={InputLabelOutlined}
                label={`Dígito${sufixoPj}`}
                name="create_account.bankAccount.agencyDv"
                normalize={normalizarDigito}
                margin="normal"
              />
            </MaterialGrid>

            <MaterialGrid item container xs={12} style={{ paddingBottom: 0 }}>
              <Typography variant="body2">Dados da conta:</Typography>
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={SelectLabel}
                label="Tipo"
                name="create_account.bankAccount.accountType"
                validate={OBRIGATORIO}
                options={TIPOS_CONTA}
                required
                margin="normal"
              />
            </MaterialGrid>
            <MaterialGrid item xs={7} md={6}>
              <Field
                component={InputLabelOutlined}
                label={`Número${sufixoPj}*`}
                name="create_account.bankAccount.accountNumber"
                validate={OBRIGATORIO}
                normalize={normalizarConta}
                required
                margin="normal"
              />
            </MaterialGrid>
            <MaterialGrid item xs={5} md={6}>
              <Field
                component={InputLabelOutlined}
                label={`Dígito${sufixoPj}*`}
                name="create_account.bankAccount.accountDv"
                normalize={normalizarDigito}
                margin="normal"
                required
                validate={OBRIGATORIO}
              />
            </MaterialGrid>
          </MaterialGrid>
        </>
      ) : null}
      <div className="row justify-content-around mb-3 mt-3">
        <PrimaryButton
          progress={parseInt(props.general.submitProgress)}
          router={props.router}
          type="submit"
          color="primary">
          {'Enviar'}
        </PrimaryButton>
      </div>
    </Form>
  );
};

GatewayForm.propTypes = {
  auth: PropTypes.shape({
    user: PropTypes.shape({
      email: PropTypes.any,
      establishments: PropTypes.any,
    }),
  }),
  change: PropTypes.func,
  formValues: PropTypes.shape({
    create_account: PropTypes.shape({
      bankAccountType: PropTypes.string,
      corporation: PropTypes.any,
    }),
  }),
  general: PropTypes.shape({
    submitProgress: PropTypes.any,
  }),
  getAdditionalData: PropTypes.func,
  handleSubmit: PropTypes.func,
  profile: PropTypes.shape({
    pagarmeData: PropTypes.shape({
      data: PropTypes.any,
      recebedor: PropTypes.any,
    }),
    pagarmeLoading: PropTypes.any,
  }),
  putGatewayInfo: PropTypes.func,
  router: PropTypes.any,
  saveGatewayInfo: PropTypes.func,
  signupCompany: PropTypes.shape({
    additionalData: PropTypes.shape({
      banks: PropTypes.shape({
        sort: PropTypes.func,
      }),
    }),
    additionalDataLoading: PropTypes.any,
  }),
};

GatewayForm = reduxForm({
  form: 'GatewayForm',
})(GatewayForm);

const mapStateToProps = (state) => {
  return {
    formValues: getFormValues('GatewayForm')(state),
    general: state.general,
    auth: state.auth,
    profile: state.profile,
    signupCompany: state.signupCompany,
  };
};
const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ getAdditionalData, saveGatewayInfo, putGatewayInfo }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(GatewayForm);
